/**
 * narrativeUtils.js
 *
 * Shared helpers for normalising AI insight narrative text.
 *
 * The backend emits markdown with custom XML tags:
 *   <good>text</good>   → positive sentiment
 *   <bad>text</bad>     → negative / risk
 *   <b>text</b>         → key number / emphasis
 *
 * This module provides:
 *   isRawMarkdown(str)      — true when str contains markdown syntax, not HTML
 *   markdownToHtml(str)     — converts that markdown to safe, portable HTML
 */

// ── Detect whether a string is raw markdown (not yet HTML) ──────────────────
export const isRawMarkdown = (str) => {
    if (!str || typeof str !== 'string') return false;
    // Has at least one markdown pattern (heading, bold, bullet, custom tag)
    return /#{1,3}\s|^\s*[-*]\s|\*\*|\<good\>|\<bad\>|\<b\>/im.test(str);
};

// ── Detect whether a string is already HTML ──────────────────────────────────
export const isHtmlNarrative = (str) => {
    if (!str || typeof str !== 'string') return false;
    return /<\w[^>]*>/.test(str);
};

// ── Convert AI markdown narrative → clean portable HTML ──────────────────────
// Safe to use in dangerouslySetInnerHTML and in the PDF iframe.
export const markdownToHtml = (raw = '') => {
    if (!raw) return '';

    let text = raw
        // Decode HTML entities first (in case text came through encoding)
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');

    // 1. Strip "Actionable Recommendations" blocks
    text = text.replace(
        /(\n|^)([-*•]\s*)?\*{0,2}Actionable\s+Recommendations\*{0,2}:?[\s\S]*?(?=\n#{1,3}\s|\n\n\n|$)/gi,
        ''
    );

    // 2. Convert custom XML sentiment tags → styled spans
    text = text
        .replace(/<good>([\s\S]*?)<\/good>/gi, '<span style="color:#16a34a;font-weight:600">$1</span>')
        .replace(/<bad>([\s\S]*?)<\/bad>/gi,  '<span style="color:#dc2626;font-weight:600">$1</span>')
        .replace(/<b>([\s\S]*?)<\/b>/gi,      '<strong>$1</strong>');

    // 3. Markdown headings → h3/h4
    text = text
        .replace(/^###\s+(.+)$/gm, '<h4 style="font-size:13px;font-weight:700;color:#7c3aed;margin:10px 0 4px 0">$1</h4>')
        .replace(/^##\s+(.+)$/gm,  '<h3 style="font-size:14px;font-weight:700;color:#7c3aed;margin:10px 0 4px 0">$1</h3>')
        .replace(/^#\s+(.+)$/gm,   '<h2 style="font-size:15px;font-weight:700;color:#7c3aed;margin:10px 0 4px 0">$1</h2>');

    // 4. Bold (**text**)
    text = text.replace(/\*\*([\s\S]*?)\*\*/g, '<strong>$1</strong>');

    // 5. Italic (*text*)
    text = text.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');

    // 6. Bullet lists — collect consecutive bullet lines into <ul>
    //    Handle lines starting with -, *, •
    text = text.replace(
        /((?:^[ \t]*[-*•]\s+.+(?:\n|$))+)/gm,
        (block) => {
            const items = block
                .split('\n')
                .map(l => l.trim())
                .filter(l => /^[-*•]\s+/.test(l))
                .map(l => `<li style="margin-bottom:3px">${l.replace(/^[-*•]\s+/, '')}</li>`)
                .join('');
            return `<ul style="padding-left:18px;margin:6px 0">${items}</ul>\n`;
        }
    );

    // 7. Remaining non-empty, non-tag lines → wrapped in <p>
    const lines = text.split('\n');
    const out = [];
    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) { out.push(''); continue; }
        // Skip lines that are already block-level HTML tags
        if (/^<(h[1-6]|ul|ol|li|p|div|blockquote)/i.test(trimmed)) {
            out.push(trimmed);
        } else {
            out.push(`<p style="margin:0 0 6px 0;line-height:1.75">${trimmed}</p>`);
        }
    }

    return out
        .join('\n')
        .replace(/\n{3,}/g, '\n\n') // collapse excess blank lines
        .trim();
};
